/**
 * Ambient muted video loop, shared by every block that wants one.
 *
 * Originally written for Feature Video; Video Band reuses it unchanged, so
 * it now binds to any `[data-mp4]` rather than one block's own class — the
 * markup contract is just that attribute plus a `.play` button with
 * `.ico-off`/`.ico-on` icons and a `.vlabel .live` caption, all styled by
 * each block's own partial. Adding a third block that wants the same
 * behaviour needs no JS changes, only that markup.
 *
 * The <video> is only created once the block approaches the viewport, so a
 * multi-megabyte MP4 never touches the initial page load. The control
 * toggles sound rather than playback: the loop is decorative.
 *
 * Two changes from the prototype:
 *
 *  - It binds to every `[data-mp4]` on the page instead of a single
 *    hardcoded id, so two of these blocks on one page do not fight over it.
 *  - It honours prefers-reduced-motion. An autoplaying looping video is
 *    exactly what that setting is asking about, so there the poster stays
 *    put and playback only starts if the viewer asks for it.
 *
 * No optional chaining or nullish coalescing: gulp-babel has no preset
 * configured, so uglify-js parses this as written.
 */
(function () {
  const blocks = document.querySelectorAll("[data-mp4]");
  if (!blocks.length) return;
  const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  function setup(root) {
    const src = root.getAttribute("data-mp4");
    if (!src) return;
    let video = null;
    function build() {
      if (video) return video;
      video = document.createElement("video");
      video.className = "frame";
      video.src = src;
      video.muted = true;
      video.loop = true;
      video.autoplay = true;
      video.preload = "auto";
      video.playsInline = true;
      // The property alone is not enough on older iOS.
      video.setAttribute("playsinline", "");
      video.setAttribute("aria-hidden", "true");

      // Behind the poster and the tint, both of which sit above it.
      root.insertBefore(video, root.firstChild);
      // Autoplay can be refused; that just leaves the poster showing.
      const started = video.play();
      if (started && typeof started.catch === "function") started.catch(function () {});
      return video;
    }
    if (!reduceMotion && "IntersectionObserver" in window) {
      const io = new IntersectionObserver(function (entries) {
        for (let i = 0; i < entries.length; i++) {
          if (entries[i].isIntersecting) {
            build();
            io.disconnect();
            return;
          }
        }
      },
      // Start fetching a little before it scrolls into view.
      {
        rootMargin: "300px"
      });
      io.observe(root);
    }
    const btn = root.querySelector(".play");
    if (!btn) return;
    btn.addEventListener("click", function () {
      const el = build();
      if (!el) return;
      if (el.muted) {
        el.muted = false;
        const resumed = el.play();
        if (resumed && typeof resumed.catch === "function") resumed.catch(function () {});
        root.classList.add("is-on");
        btn.setAttribute("aria-label", "Mute video");
      } else {
        el.muted = true;
        root.classList.remove("is-on");
        btn.setAttribute("aria-label", "Unmute video");
      }
    });
  }
  for (let i = 0; i < blocks.length; i++) {
    setup(blocks[i]);
  }
})();